<script setup lang="ts">
/**
 * Presentational list for the investor bank-accounts directory.
 * Receives rows + filter-count via props; emits row-click / view-
 * transactions / update:page / filter-click. No queries, no mutations,
 * no router pushes — orchestration lives in the container.
 *
 * Rows are clickable (open detail drawer); a row-actions kebab carries
 * a sibling "View transactions" action so the operator can skip the
 * drawer when the only intent is to inspect the bank account's ledger.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import DataTableV2, { type TableAction, type TableColumn } from '@uiKit/components/DataTableV2.vue'
import { formatDate, fullName, maskAccountNumber } from '@shared/utils'
import {
  getBankAccountStatusColor,
  getBankAccountStatusLabel,
} from '../utils/bankAccountsDirectoryFormatters'
import type { BankAccountRow } from '../types/bankAccountsDirectoryTypes'

defineProps<{
  rows: BankAccountRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  title?: string
  subtitle?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'filter-click'): void
  (e: 'row-click', row: BankAccountRow): void
  (e: 'view-details', row: BankAccountRow): void
  (e: 'view-transactions', row: BankAccountRow): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: 'minmax(220px, 1.6fr)' },
  { key: 'bank', label: 'BANK', width: 'minmax(160px, 1fr)' },
  { key: 'accountHolder', label: 'ACCOUNT HOLDER', width: 'minmax(180px, 1fr)' },
  { key: 'accountNumber', label: 'ACCOUNT NUMBER', width: 'minmax(160px, 1fr)' },
  { key: 'status', label: 'STATUS', width: 'minmax(160px, 1fr)' },
  { key: 'createdAt', label: 'ADDED', width: 'minmax(140px, 1fr)' },
]

const rowActions: TableAction<BankAccountRow>[] = [
  {
    label: 'View details',
    icon: 'eye',
    onClick: (row) => emit('view-details', row),
  },
  {
    label: 'View transactions',
    icon: 'arrow-right',
    onClick: (row) => emit('view-transactions', row),
  },
]

function investorName(row: BankAccountRow): string {
  return fullName(row.investor.firstName, row.investor.lastName) || '—'
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :actions="rowActions"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.bankAccountsDirectory.pageSize"
    clickable
    :empty-title="emptyTitle ?? 'No bank accounts'"
    :empty-subtitle="emptySubtitle ?? 'Bank accounts matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @filter-click="emit('filter-click')"
    @row-click="emit('row-click', $event as BankAccountRow)"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{ investorName(row as BankAccountRow) }}</Text>
        <Text type="caption" color="muted">
          {{ (row as BankAccountRow).investor.investorType || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-bank="{ row }">
      <Text type="body-sm">{{ (row as BankAccountRow).bankName || '—' }}</Text>
    </template>

    <template #cell-accountHolder="{ row }">
      <Text type="body-sm">{{ (row as BankAccountRow).accountHolderName || '—' }}</Text>
    </template>

    <template #cell-accountNumber="{ row }">
      <Text type="body-sm" color="muted" style="font-variant-numeric: tabular-nums">
        {{ maskAccountNumber((row as BankAccountRow).accountNumber) || '—' }}
      </Text>
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="getBankAccountStatusColor((row as BankAccountRow).accountStatus)"
        :label="getBankAccountStatusLabel((row as BankAccountRow).accountStatus)"
      />
    </template>

    <template #cell-createdAt="{ row }">
      <Text type="body-sm" color="muted">{{ formatDate((row as BankAccountRow).createdAt) }}</Text>
    </template>
  </DataTableV2>
</template>
